import { useEffect, useRef } from "react";
import SEOHead from "@/components/SEOHead";
import HomeContent from "./HomeContent";
import { initializeHomeMotion } from "./homeMotion";
import "./home.css";
import { getHomeCopy, type HomeLanguage } from "./homeTranslations";

const HREFLANG = [
  { lang: "fr", href: "https://htmcagency.com/" },
  { lang: "ca", href: "https://htmcagency.com/ca/" },
  { lang: "en", href: "https://htmcagency.com/en/" },
  { lang: "x-default", href: "https://htmcagency.com/" },
];

const HOME_META = {
  fr: {
    title: "Agence web en Andorre : sites, SEO, Google Ads | HTMC",
    description: "Agence web en Andorre pour les PME d'Andorre et de France : création de sites, SEO et Google Ads (engagement de 3 mois). Audit gratuit.",
    canonical: "https://htmcagency.com/",
  },
  en: {
    title: "Web Agency in Andorra & France — Web Design, SEO, Google Ads",
    description: "Andorra-based web agency serving France. Websites, SEO and Google Ads. Google Ads management with a 3-month commitment. Free audit.",
    canonical: "https://htmcagency.com/en/",
  },
  ca: {
    title: "HTMC Agency — Agència web Andorra & França | Creació de lloc, SEO, Google Ads",
    description: "Agència web amb seu a Andorra, activa a França. Webs, SEO i Google Ads. Gestió de Google Ads amb compromís de 3 mesos. Auditoria gratuïta.",
    canonical: "https://htmcagency.com/ca/",
  },
};

export default function HomePage({ language = "fr" }: { language?: HomeLanguage }) {
  const meta = HOME_META[language];
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!root.current) return;
    const cleanup = initializeHomeMotion(root.current, getHomeCopy(language));
    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.dataset.schema = "faq-homepage";
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: Array.from(root.current.querySelectorAll(".faq details")).map(item => ({
        "@type": "Question",
        name: item.querySelector("summary")?.textContent.trim(),
        acceptedAnswer: { "@type": "Answer", text: item.querySelector("p")?.textContent.trim() },
      })),
    });
    document.head.append(schema);
    return () => { cleanup(); schema.remove(); };
  }, [language]);

  return (
    <div className="htmc-home" lang={language} ref={root}>
      <SEOHead
        title={meta.title}
        description={meta.description}
        canonical={meta.canonical}
        lang={language}
        hreflang={HREFLANG}
      />
      <HomeContent language={language} />
    </div>
  );
}
